// pomodoro.ts - Modern Glassmorphic Circular Pomodoro Timer Logic with Real-time Multi-device Sync
document.addEventListener('DOMContentLoaded', () => {
    const timerDisplay = document.getElementById('pomodoro-timer-display');
    const startBtn = document.getElementById('pomodoro-start-btn');
    const pauseBtn = document.getElementById('pomodoro-pause-btn');
    const resetBtn = document.getElementById('pomodoro-reset-btn');
    const add5Btn = document.getElementById('pomodoro-add5-btn');
    const quickStartBtn = document.getElementById('pomodoro-quick-start-btn');
    const statsTbody = document.getElementById('pomodoro-stats-table-tbody');
    const progressCircle = document.getElementById('pomodoro-progress-circle') as unknown as SVGGeometryElement | null;
    const modeLabelEl = document.getElementById('pomodoro-mode-label');
    const statusHintEl = document.getElementById('pomodoro-status-hint');
    const completedCountEl = document.getElementById('pomo-completed-count');
    const completedMinutesEl = document.getElementById('pomo-completed-minutes');
    const modeButtons = document.querySelectorAll('.pomo-mode-btn');

    if (!timerDisplay) return;

    const CIRCUMFERENCE = 1000; // Matches pathLength="1000" on rounded-square SVG path
    const STORAGE_KEY = 'syncflow_pomo_state';

    const modeLabels: Record<string, string> = {
        focus: 'Фокус сесія',
        shortBreak: 'Коротка перерва',
        longBreak: 'Тривалий відпочинок',
        deepWork: 'Глибока праця'
    };

    let totalDuration = 25 * 60;
    let timeLeft = totalDuration;
    let elapsedSeconds = 0;
    let startedAt: number | null = null;
    let timerId: any = null;
    let isRunning = false;
    let currentMode = 'focus';
    let currentModeName = 'Фокус сесія';
    let isSyncingFromServer = false;

    function getLiveElapsed(): number {
        let el = elapsedSeconds;
        if (isRunning && startedAt) {
            const now = Date.now();
            el += Math.max(0, Math.floor((now - startedAt) / 1000));
        }
        return Math.min(el, totalDuration);
    }

    function saveLocalState() {
        try {
            const state = {
                mode: currentMode,
                totalDuration,
                elapsedSeconds: getLiveElapsed(),
                isRunning,
                startedAt: isRunning ? startedAt : null,
                savedAt: Date.now()
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (e) {}
    }

    function loadLocalState() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return false;
            const state = JSON.parse(raw);
            if (!state || typeof state !== 'object') return false;

            currentMode = state.mode || 'focus';
            currentModeName = modeLabels[currentMode] || 'Фокус сесія';
            totalDuration = Number(state.totalDuration) || 25 * 60;

            const baseElapsed = Number(state.elapsedSeconds) || 0;

            if (state.isRunning && state.startedAt) {
                const now = Date.now();
                const totalElapsed = baseElapsed + Math.max(0, Math.floor((now - state.startedAt) / 1000));
                if (totalElapsed >= totalDuration) {
                    // Session finished while page was closed
                    elapsedSeconds = 0;
                    isRunning = false;
                    startedAt = null;
                    timeLeft = totalDuration;
                } else {
                    elapsedSeconds = baseElapsed;
                    startedAt = state.startedAt;
                    isRunning = true;
                    timeLeft = Math.max(0, totalDuration - totalElapsed);
                    if (!timerId) timerId = setInterval(tick, 1000);
                }
            } else {
                isRunning = false;
                startedAt = null;
                elapsedSeconds = Math.min(baseElapsed, totalDuration);
                timeLeft = Math.max(0, totalDuration - elapsedSeconds);
            }

            updateModeUI(currentMode);
            return true;
        } catch (e) {
            return false;
        }
    }

    function updateRingProgress() {
        if (!progressCircle) return;
        const liveLeft = Math.max(0, totalDuration - getLiveElapsed());
        const progress = Math.max(0, Math.min(1, liveLeft / totalDuration));
        const offset = CIRCUMFERENCE * (1 - progress);
        progressCircle.style.strokeDashoffset = `${offset}`;
    }

    function updateModeUI(mode: string) {
        modeButtons.forEach(btn => {
            if (btn.getAttribute('data-mode') === mode) {
                btn.classList.add('active');
            } else {
                btn.classList.remove('active');
            }
        });

        const container = document.querySelector('.pomodoro-container');
        if (container) {
            container.classList.remove('mode-break', 'mode-deep', 'mode-focus');
            if (mode === 'shortBreak' || mode === 'longBreak') {
                container.classList.add('mode-break');
            } else if (mode === 'deepWork') {
                container.classList.add('mode-deep');
            } else {
                container.classList.add('mode-focus');
            }
        }
    }

    function updateDisplay() {
        if (!timerDisplay) return;
        const curLeft = Math.max(0, totalDuration - getLiveElapsed());
        timeLeft = curLeft;

        const m = Math.floor(curLeft / 60).toString().padStart(2, '0');
        const s = (curLeft % 60).toString().padStart(2, '0');
        timerDisplay.innerText = `${m}:${s}`;

        updateRingProgress();

        if (modeLabelEl) modeLabelEl.innerText = currentModeName;

        const container = document.querySelector('.pomodoro-container');
        if (isRunning) {
            if (startBtn) startBtn.style.display = 'none';
            if (pauseBtn) pauseBtn.style.display = 'inline-flex';
            if (container) container.classList.add('is-running');
            document.title = `[${m}:${s}] ${currentModeName} | SyncFlow`;
            if (statusHintEl) statusHintEl.innerText = 'Сесія триває...';
            (window as any).setSidebarBadge?.('pomodoro', `${m}:${s}`, 'live', `${currentModeName} триває`);
        } else {
            if (startBtn) startBtn.style.display = 'inline-flex';
            if (pauseBtn) pauseBtn.style.display = 'none';
            if (container) container.classList.remove('is-running');
            document.title = `SyncFlow`;

            const todayCount = completedCountEl ? parseInt(completedCountEl.innerText || '0', 10) || 0 : 0;
            if (curLeft < totalDuration && curLeft > 0) {
                if (statusHintEl) statusHintEl.innerText = 'Сесію призупинено';
                (window as any).setSidebarBadge?.('pomodoro', `${m}:${s}`, 'warning', 'Сесію Помодоро призупинено');
            } else {
                if (statusHintEl) statusHintEl.innerText = 'Натисніть Старт для початку';
                (window as any).setSidebarBadge?.(
                    'pomodoro',
                    todayCount > 0 ? todayCount : null,
                    'success',
                    todayCount > 0 ? `Завершено сесій Помодоро сьогодні: ${todayCount}` : ''
                );
            }
        }
    }

    function setMode(mode: string, durationMinutes: number, syncServer = true) {
        pauseTimer(false);
        currentMode = mode;
        currentModeName = modeLabels[mode] || 'Фокус сесія';
        totalDuration = durationMinutes * 60;
        elapsedSeconds = 0;
        startedAt = null;
        timeLeft = totalDuration;

        updateModeUI(mode);
        saveLocalState();
        updateDisplay();

        if (syncServer) {
            notifyServer('set_mode', { mode, duration_seconds: totalDuration });
        }
    }

    modeButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            const mode = btn.getAttribute('data-mode') || 'focus';
            const time = parseInt(btn.getAttribute('data-time') || '25', 10);
            setMode(mode, time);
        });
    });

    async function notifyServer(action: string, extra: any = {}) {
        try {
            await fetch('api/timers.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    timer_type: 'pomodoro',
                    action,
                    mode: currentMode,
                    duration_seconds: totalDuration,
                    elapsed_seconds: getLiveElapsed(),
                    ...extra
                })
            });
        } catch (e) {
            console.warn('Pomodoro sync error:', e);
        }
    }

    async function syncFromServer() {
        if (isSyncingFromServer) return;
        try {
            isSyncingFromServer = true;
            const res = await fetch('api/timers.php?type=pomodoro');
            const data = await res.json();
            if (data.success && data.timers && data.timers.pomodoro) {
                applyServerState(data.timers.pomodoro);
            }
        } catch (e) {
            // Silently ignore background polling errors
        } finally {
            isSyncingFromServer = false;
        }
    }

    function applyServerState(s: any) {
        const serverIsRunning = Boolean(s.is_running);
        const serverMode = s.mode || 'focus';
        const serverDuration = Number(s.duration_seconds) || 1500;
        const serverElapsed = Number(s.current_elapsed) || 0;

        let needsDisplayUpdate = false;

        // Remote mode switch
        if (serverMode !== currentMode || serverDuration !== totalDuration) {
            currentMode = serverMode;
            currentModeName = modeLabels[serverMode] || 'Фокус сесія';
            totalDuration = serverDuration;
            updateModeUI(currentMode);
            needsDisplayUpdate = true;
        }

        if (serverIsRunning) {
            if (!isRunning) {
                // Started on another device!
                isRunning = true;
                elapsedSeconds = Number(s.elapsed_seconds) || 0;
                startedAt = Date.now() - (serverElapsed - elapsedSeconds) * 1000;
                if (!timerId) timerId = setInterval(tick, 1000);
                needsDisplayUpdate = true;
            } else {
                // Drift correction
                const localEl = getLiveElapsed();
                if (Math.abs(localEl - serverElapsed) > 2) {
                    elapsedSeconds = Number(s.elapsed_seconds) || 0;
                    startedAt = Date.now() - (serverElapsed - elapsedSeconds) * 1000;
                    needsDisplayUpdate = true;
                }
            }
        } else {
            if (isRunning) {
                // Paused or reset on another device
                isRunning = false;
                if (timerId) clearInterval(timerId);
                timerId = null;
                startedAt = null;
                needsDisplayUpdate = true;
            }
            if (Math.abs(elapsedSeconds - serverElapsed) > 1) {
                elapsedSeconds = serverElapsed;
                needsDisplayUpdate = true;
            }
        }

        if (needsDisplayUpdate) {
            saveLocalState();
            updateDisplay();
        }
    }

    function tick() {
        const curElapsed = getLiveElapsed();
        timeLeft = Math.max(0, totalDuration - curElapsed);
        updateDisplay();
        saveLocalState();

        if (curElapsed >= totalDuration) {
            clearInterval(timerId);
            timerId = null;
            isRunning = false;
            startedAt = null;
            elapsedSeconds = 0;
            timeLeft = totalDuration;
            saveLocalState();
            updateDisplay();

            notifyServer('reset', { duration_seconds: totalDuration });
            saveSession();

            try {
                // Browser notification if permitted
                if ('Notification' in window && Notification.permission === 'granted') {
                    new Notification('SyncFlow Помодоро', {
                        body: `🎉 Сесію "${currentModeName}" завершено! Час відпочити або переключитись.`
                    });
                }
            } catch (e) {}
        }
    }

    function startTimer() {
        if (!isRunning) {
            isRunning = true;
            startedAt = Date.now();
            timerId = setInterval(tick, 1000);
            saveLocalState();
            updateDisplay();
            notifyServer('start', {
                mode: currentMode,
                duration_seconds: totalDuration,
                elapsed_seconds: elapsedSeconds
            });
        }
    }

    function pauseTimer(syncServer = true) {
        if (isRunning) {
            elapsedSeconds = getLiveElapsed();
            isRunning = false;
            startedAt = null;
            if (timerId) clearInterval(timerId);
            timerId = null;
            saveLocalState();
            updateDisplay();

            if (syncServer) {
                notifyServer('pause', {
                    elapsed_seconds: elapsedSeconds
                });
            }
        }
    }

    if (startBtn) startBtn.onclick = startTimer;
    if (pauseBtn) pauseBtn.onclick = () => pauseTimer(true);

    if (resetBtn) {
        resetBtn.onclick = () => {
            pauseTimer(false);
            elapsedSeconds = 0;
            startedAt = null;
            timeLeft = totalDuration;
            saveLocalState();
            updateDisplay();
            notifyServer('reset', { duration_seconds: totalDuration });
        };
    }

    if (add5Btn) {
        add5Btn.onclick = () => {
            totalDuration += 5 * 60;
            saveLocalState();
            updateDisplay();
            notifyServer('add_time', { add_seconds: 300, duration_seconds: totalDuration });
        };
    }

    if (quickStartBtn) {
        quickStartBtn.onclick = () => {
            const timerSubBtn = document.querySelector('.sub-tab-button[data-sub-tab="pomodoro-timer"]') as HTMLElement | null;
            if (timerSubBtn) timerSubBtn.click();
            startTimer();
        };
    }

    async function loadPomodoroStats() {
        (window as any).loadPomodoroStats = loadPomodoroStats;
        if (!statsTbody) return;
        try {
            const res = await fetch('api/pomodoro.php?action=list');
            const data = await res.json();
            if (data.success && Array.isArray(data.data)) {
                renderPomodoroStats(data.data);
                updateDailySummary(data.data);
            }
        } catch (e) {
            console.error('Failed to load pomodoro stats', e);
        }
    }

    function updateDailySummary(sessions: any[]) {
        const todayStr = new Date().toISOString().slice(0, 10);
        const todaySessions = sessions.filter(s => s.completed_at && s.completed_at.startsWith(todayStr));
        const count = todaySessions.length;
        const totalMins = todaySessions.reduce((acc, s) => acc + (parseInt(s.duration_minutes, 10) || 25), 0);

        if (completedCountEl) completedCountEl.innerText = `${count}`;
        if (completedMinutesEl) completedMinutesEl.innerText = `${totalMins} хв`;
        if (!isRunning && (timeLeft === totalDuration || timeLeft <= 0)) {
            (window as any).setSidebarBadge?.(
                'pomodoro',
                count > 0 ? count : null,
                'success',
                count > 0 ? `Завершено сесій Помодоро сьогодні: ${count}` : ''
            );
        }
    }

    function renderPomodoroStats(sessions: any[]) {
        if (!statsTbody) return;
        statsTbody.innerHTML = '';
        if (sessions.length === 0) {
            statsTbody.innerHTML = `<tr><td colspan="3" style="text-align: center; color: var(--text-dim); padding: 24px;">Завершених сесій Помодоро поки немає</td></tr>`;
            return;
        }

        sessions.forEach(session => {
            const tr = document.createElement('tr');
            const dateStr = session.completed_at ? new Date(session.completed_at).toLocaleString('uk-UA') : '—';
            const durationStr = `${session.duration_minutes || 25} хв`;
            const notesStr = session.notes || 'Сесія фокусу';

            tr.innerHTML = `
                <td class="col-date">${dateStr}</td>
                <td class="col-limit"><span class="badge badge-info" style="padding: 4px 8px; border-radius: 4px; font-weight: 500; font-size: 12px;"><i class="ri-timer-line"></i> ${durationStr}</span></td>
                <td class="col-name">${escapeHtml(notesStr)}</td>
            `;
            statsTbody.appendChild(tr);
        });
    }

    async function saveSession() {
        try {
            const mins = Math.round(totalDuration / 60);
            await fetch('api/pomodoro.php?action=create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ duration_minutes: mins, notes: `${currentModeName} (${mins} хв)` })
            });
            if ((window as any).showMessage) (window as any).showMessage('Сесію Помодоро успішно завершено та збережено!', 'success');
            loadPomodoroStats();
        } catch (e) {
            console.error('Failed to save pomodoro', e);
        }
    }

    function escapeHtml(str: string) {
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    const pomodoroTabBtn = document.querySelector('button[data-tab="pomodoro"]');
    if (pomodoroTabBtn) {
        pomodoroTabBtn.addEventListener('click', () => {
            loadPomodoroStats();
            syncFromServer();
        });
    }

    const statsSubTabBtn = document.querySelector('.sub-tab-button[data-sub-tab="pomodoro-stats"]');
    if (statsSubTabBtn) {
        statsSubTabBtn.addEventListener('click', () => {
            loadPomodoroStats();
        });
    }

    // 1. Instant local restore (zero flicker on Ctrl+R)
    loadLocalState();
    updateDisplay();
    loadPomodoroStats();

    // 2. Initial background sync with server
    syncFromServer();

    // 3. Periodic polling every 4 seconds when tab is active (multi-device phone <-> laptop sync)
    setInterval(() => {
        if (!document.hidden) {
            syncFromServer();
        }
    }, 4000);

    // 4. Sync immediately when user switches tabs or focuses window
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            syncFromServer();
        }
    });
    window.addEventListener('focus', () => {
        syncFromServer();
    });
});
